import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    category: 'Réservation',
    icon: 'ri-calendar-check-line',
    questions: [
      {
        question: 'Comment réserver un transfert ?',
        answer: 'Vous pouvez réserver directement en ligne via notre formulaire de réservation, par téléphone au +33 6 XX XX XX XX, ou par email. La réservation est confirmée instantanément.'
      },
      {
        question: 'Puis-je modifier ou annuler ma réservation ?',
        answer: 'Oui, vous pouvez modifier ou annuler votre réservation jusqu\'à 24h avant le départ sans frais. Au-delà, des frais d\'annulation peuvent s\'appliquer.'
      },
      {
        question: 'Quel est le délai de réservation minimum ?',
        answer: 'Nous acceptons les réservations jusqu\'à 2 heures avant le départ, sous réserve de disponibilité. Pour garantir votre créneau, nous recommandons de réserver au moins 24h à l\'avance.'
      }
    ]
  },
  {
    category: 'Tarifs',
    icon: 'ri-money-euro-circle-line',
    questions: [
      {
        question: 'Comment sont calculés les tarifs ?',
        answer: 'Nos tarifs sont fixes et calculés en fonction de la distance, du type de véhicule et de l\'horaire (jour/nuit). Aucun frais caché, le prix affiché est le prix final.'
      },
      {
        question: 'Quels modes de paiement acceptez-vous ?',
        answer: 'Nous acceptons les paiements par carte bancaire, virement, espèces et les principaux moyens de paiement en ligne. Le paiement peut être effectué à la réservation ou à bord.'
      },
      {
        question: 'Y a-t-il des frais supplémentaires ?',
        answer: 'Non, tous nos tarifs sont tout compris : bagages, équipement ski, sièges enfants, eau et WiFi. Seul un supplément de 20% s\'applique pour les trajets de nuit (19h-7h).'
      }
    ]
  },
  {
    category: 'Service',
    icon: 'ri-customer-service-2-line',
    questions: [
      {
        question: 'Que se passe-t-il en cas de retard de vol ?',
        answer: 'Nous suivons votre vol en temps réel. En cas de retard, votre chauffeur ajuste automatiquement l\'heure de prise en charge. L\'attente est incluse dans le tarif pour les transferts aéroport.'
      },
      {
        question: 'Combien de bagages puis-je emporter ?',
        answer: 'Pour une berline : jusqu\'à 3 valises + équipement ski. Pour un van : jusqu\'à 7 valises + équipement ski. Des bagages supplémentaires peuvent être acceptés sur demande.'
      },
      {
        question: 'Proposez-vous des sièges enfants ?',
        answer: 'Oui, nous fournissons gratuitement des sièges enfants (bébé, rehausseur) sur demande lors de la réservation. Précisez l\'âge et le poids de l\'enfant.'
      }
    ]
  }
];

export default function FAQSection() {
  const [openCategory, setOpenCategory] = useState<number | null>(0);
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  const toggleQuestion = (categoryIndex: number, questionIndex: number) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenQuestion(openQuestion === key ? null : key);
  };

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-white">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-voyage-gold/10 px-6 py-3 rounded-full mb-6"
          >
            <i className="ri-question-line text-voyage-gold"></i>
            <span className="text-night-blue font-inter text-sm font-semibold uppercase tracking-wider">FAQ</span>
          </motion.div>

          <h2 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-night-blue mb-4 sm:mb-6">
            Questions fréquentes
          </h2>
          <p className="text-slate-600 font-inter text-base sm:text-lg lg:text-xl max-w-2xl mx-auto">
            Trouvez rapidement les réponses à vos questions
          </p>
        </motion.div>

        {/* Catégories */}
        <div className="space-y-6 sm:space-y-8">
          {faqs.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: categoryIndex * 0.1 }}
              className="bg-gradient-to-br from-slate-50 to-white rounded-2xl sm:rounded-3xl overflow-hidden border border-slate-200 shadow-lg"
            >
              {/* En-tête de catégorie */}
              <motion.button
                onClick={() => setOpenCategory(openCategory === categoryIndex ? null : categoryIndex)}
                className="w-full px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between bg-gradient-to-r from-night-blue to-slate-900 hover:from-voyage-gold hover:to-amber-600 transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-white/20 transition-all">
                    <i className={`${category.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="font-playfair text-xl sm:text-2xl font-bold text-white text-left">
                    {category.category}
                  </h3>
                </div>
                <motion.i
                  animate={{ rotate: openCategory === categoryIndex ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="ri-arrow-down-s-line text-white text-3xl"
                ></motion.i>
              </motion.button>

              {/* Questions */}
              <AnimatePresence>
                {openCategory === categoryIndex && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                      {category.questions.map((faq, questionIndex) => {
                        const key = `${categoryIndex}-${questionIndex}`;
                        const isOpen = openQuestion === key;

                        return (
                          <motion.div
                            key={questionIndex}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: questionIndex * 0.05 }}
                            className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-voyage-gold/50 transition-all"
                          >
                            <button
                              onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                              className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-start justify-between gap-4 text-left hover:bg-slate-50 transition-all cursor-pointer group"
                            >
                              <div className="flex items-start gap-3 flex-1">
                                <div className="w-8 h-8 bg-voyage-gold/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-voyage-gold/20 transition-all">
                                  <i className="ri-question-line text-voyage-gold text-lg"></i>
                                </div>
                                <span className="font-inter font-semibold text-night-blue text-sm sm:text-base leading-relaxed">
                                  {faq.question}
                                </span>
                              </div>
                              <motion.i
                                animate={{ rotate: isOpen ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                                className="ri-arrow-down-s-line text-slate-400 text-2xl flex-shrink-0"
                              ></motion.i>
                            </button>

                            <AnimatePresence>
                              {isOpen && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-5 sm:px-6 pb-4 sm:pb-5 pl-14 sm:pl-16">
                                    <p className="text-slate-600 font-inter text-sm sm:text-base leading-relaxed">
                                      {faq.answer}
                                    </p>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* CTA Contact */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 sm:mt-16 bg-gradient-to-br from-slate-50 to-white rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center border border-slate-200"
        >
          <div className="w-16 h-16 bg-voyage-gold/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <i className="ri-customer-service-2-line text-voyage-gold text-3xl"></i>
          </div>
          <h3 className="font-playfair text-2xl sm:text-3xl font-bold text-night-blue mb-4">
            Vous ne trouvez pas votre réponse ?
          </h3>
          <p className="text-slate-600 font-inter text-base sm:text-lg mb-8 max-w-2xl mx-auto">
            Notre équipe est disponible 24/7 pour répondre à toutes vos questions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href="tel:+33600000000"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-night-blue to-slate-900 text-white px-8 py-4 rounded-full font-inter font-bold text-base hover:from-voyage-gold hover:to-amber-600 transition-all whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-3 shadow-lg"
            >
              <i className="ri-phone-line text-xl"></i>
              Appelez-nous
            </motion.a>
            <motion.a
              href="mailto:contact@mptransfer.fr"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-night-blue px-8 py-4 rounded-full font-inter font-semibold text-base border-2 border-slate-300 hover:border-voyage-gold hover:bg-voyage-gold/5 transition-all whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-3"
            >
              <i className="ri-mail-line text-xl"></i>
              Envoyez un email
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}